import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnamesBind from 'classnames/bind';
import Icon from '@concur/nui-widgets/Icon';
import List from '@concur/nui-widgets/List';
import Link from '@concur/nui-widgets/Link';
import Button from '@concur/nui-widgets/Button';
import isEnterOrSpace from '@concur/nui-widgets/lib/utils/isEnterOrSpace';
import { withFormatter, getLanguage } from '@concur/nui-intl-runtime';
import { withErrorBoundary, compose, getDataCenter } from '@concur/core-ui-shell';
import NavMenu from '@concur/nui-widgets/NavMenu';
import ProfileIdentifier from './_ProfileIdentifier';
import ActingAs from './_ActingAs';
import userProfileStyles from './UserProfileMenu.css';
import ServiceStatusLink from '../AppFooter/_ServiceStatusLink';
import clearLocalizationMode from '../utils/clearLocalizationMode';
import { clearSideNavState } from '../utils/sideNavStateUtils';
import { clearSessionStorage } from '../utils/sessionStorageUtils';
import { destroySDK } from '../utils/builtInSupport';

const CSS_BLOCK = 'sapcnqr-user-profile-menu';
const CHINA_DATA_CENTER = 'china';

const UserProfileMenu = ({
    actingAs = {},
    baseUrl,
    formatter,
    styles,
    onEndLoading,
    onStartLoading,
    profileUrl = '#',
    actingAsQuickHelpRef,
    actingAsUserDropdownRef,
    oshCacheIdentifier,
    lang,
    userProfileMenuProps = {},
    showAboutDialogCallback,
    isOpusUser,
    isCompleteTheme,
}) => {
    const classnames = classnamesBind.bind({ ...styles, ...userProfileStyles });

    const { allUsers, companyName, otherUserName } = actingAs;
    const {
        isTravelUser,
        supportLink,
        controllerPrivacyStatementUrl,
        dataCenterLocation,
        customerLegalDisclosureUrl,
        demo,
    } = userProfileMenuProps;

    const isActingForOthers = !!(allUsers || companyName || otherUserName);

    const [actingAsExpanded, setIsActingAsExpanded] = useState(isActingForOthers);
    const [legalOpen, setLegalOpen] = useState(false);
    const [asyncCookiePrefLink, setAsyncCookiePrefLink] = useState(null);

    useEffect(() => {
        const linkProps = {
            role: 'menuitem',
            'data-analytics-json':
                '{"applicationName":"userprofilemenu","elementPath":"cookie-preferences-link","action":"click","elementType":"link"}',
            'data-test': 'cookie-preferences-link',
            className: classnames(
                `${CSS_BLOCK}__list-item-content`,
                `${CSS_BLOCK}__list-item-content--trustarc`,
            ),
        };
        if (demo) {
            setAsyncCookiePrefLink(
                <List.Item
                    className={classnames(`${CSS_BLOCK}__list-item`)}
                >
                    <div {...linkProps} id="teconsent">
                        <a href="#teconsent">Cookies Preferences (Demo)</a>
                    </div>
                </List.Item>,
            );
            return;
        }
        const teConsentElem = document.getElementById('teconsent');
        if (teConsentElem) {
            setAsyncCookiePrefLink(
                <List.Item
                    className={classnames(`${CSS_BLOCK}__list-item`)}
                    role="none"
                    onClick={() => {
                        window?.truste?.eu?.prefButton?.click();
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            window?.truste?.eu?.prefButton?.click();
                        }
                    }}
                >
                    <div
                        {...linkProps}
                        // Couldn't find a way around this.
                        // Even if we do not dangerouslySetInnerHTML,
                        // TrustArc is it doing it any way to insert its content.
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: teConsentElem.innerHTML }}
                    />
                </List.Item>,
            );
        } else {
            setAsyncCookiePrefLink(null);
        }
    }, []);

    const isProfileDisabled = !profileUrl || profileUrl.trim() === '#';

    const dataCenter = getDataCenter(dataCenterLocation);

    const getCookieStatementUrl = () => {
        const supportedLangs = [
            'bg',
            'cs',
            'da',
            'de',
            'el',
            'es',
            'es-la',
            'en-gb',
            'fi',
            'fr',
            'fr-ca',
            'hr',
            'hu',
            'id',
            'it',
            'ja',
            'ko',
            'nl',
            'no',
            'ms',
            'pl',
            'pt-br',
            'ro',
            'ru',
            'sk',
            'sl',
            'sv',
            'th',
            'tr',
            'uk',
            'vi',
            'zh-cn',
            'zh-tw',
        ];

        const langUrl = supportedLangs.includes(getLanguage(lang)) ? getLanguage(lang) : 'en';
        const cdnUrl = dataCenter === CHINA_DATA_CENTER ? 'static.concurcdn.cn' : 'static.concursolutions.com';
        return `https://${cdnUrl}/about/legal/privacy/${langUrl}/`;
    };

    const onLogOut = () => {
        clearLocalizationMode();
        clearSideNavState();
        clearSessionStorage(oshCacheIdentifier);
        destroySDK();
        if (baseUrl) {
            window.location.href = `${baseUrl}/nui-auth/api/signout?signedout=manual`;
        } else {
            window.location.href = '/errors/loggedout.asp';
        }
    };

    return (
        <div className={classnames(CSS_BLOCK)} data-test="user-profile-menu">
            <ProfileIdentifier
                actingAs={actingAs}
                cssBlock={CSS_BLOCK}
                lang={lang}
                styles={{ ...styles, ...userProfileStyles }}
                isOpusUser={isOpusUser}
                isCompleteTheme={isCompleteTheme}
            />
            <List
                aria-label={formatter.formattedMessage({ id: 'CoreUI.profile.profileMenu' })}
                className={classnames(`${CSS_BLOCK}__list`)}
            >
                <List.Item className={classnames(`${CSS_BLOCK}__list-item`)}>
                    <Link
                        disabled={isProfileDisabled}
                        className={classnames(`${CSS_BLOCK}__list-item-content`, {
                            [`${CSS_BLOCK}__list-item-content--disabled`]: isProfileDisabled,
                        })}
                        data-test="user-profile-menu-profile-link"
                        innerTextClasses={classnames(`${CSS_BLOCK}__list-item-content-text`)}
                        href={profileUrl !== '#' ? profileUrl : undefined}
                        onKeyDown={() => { }}
                        tabIndex={isProfileDisabled ? -1 : 0}
                    >
                        <Icon
                            ariaHidden
                            className={classnames(`${CSS_BLOCK}__icon`)}
                            iconName="gear"
                            size="sm"
                        />
                        {formatter.formattedMessage({ id: 'CoreUI.profile.profileSettings' })}
                    </Link>
                </List.Item>
                {(actingAs.options?.length > 0 || isActingForOthers) && (
                    <List.Item
                        aria-label={
                            !actingAsExpanded
                                ? formatter.formattedMessage({ id: 'CoreUI.actingAsExpand' })
                                : formatter.formattedMessage({ id: 'CoreUI.actingAsCollapse' })
                        }
                        aria-expanded={actingAsExpanded}
                        data-test="user-profile-menu-acting-as"
                        className={classnames(`${CSS_BLOCK}__list-item`)}
                    >
                        <span
                            className={classnames(`${CSS_BLOCK}__list-item-content`)}
                            onClick={() => {
                                setIsActingAsExpanded(!actingAsExpanded);
                            }}
                            onKeyDown={(event) => {
                                if (isEnterOrSpace(event)) {
                                    setIsActingAsExpanded(!actingAsExpanded);
                                    event.preventDefault();
                                    event.stopPropagation();
                                }
                            }}
                            role="button"
                            tabIndex={0}
                        >
                            <Icon
                                ariaHidden
                                className={classnames(`${CSS_BLOCK}__icon`)}
                                iconName="globe"
                                size="sm"
                            />
                            {formatter.formattedMessage({ id: 'CoreUI.actAsAnotherUser' })}
                            <Icon
                                ariaHidden
                                className={classnames(
                                    `${CSS_BLOCK}__icon`,
                                    `${CSS_BLOCK}__acting-as-trigger-icon`,
                                )}
                                iconName={actingAsExpanded ? 'slim-arrow-up' : 'slim-arrow-down'}
                                forceSize="sm"
                            />
                        </span>
                        {actingAsExpanded && (
                            <div className={classnames(`${CSS_BLOCK}__acting-as`)}>
                                <ActingAs
                                    isActingForOthers={isActingForOthers}
                                    onEndLoading={onEndLoading}
                                    onStartLoading={onStartLoading}
                                    options={actingAs.options}
                                    quickHelpRef={actingAsQuickHelpRef}
                                    userDropdownRef={actingAsUserDropdownRef}
                                />
                            </div>
                        )}
                    </List.Item>
                )}
                {isTravelUser && (
                    <List.Item
                        className={classnames(`${CSS_BLOCK}__list-item`)}
                        data-test="user-profile-menu-travel-policy-link"
                    >
                        <Link
                            className={classnames(`${CSS_BLOCK}__list-item-content`)}
                            innerTextClasses={classnames(`${CSS_BLOCK}__list-item-content-text`)}
                            data-height="540"
                            data-name="policy"
                            data-toggle="popupwindow"
                            data-url="/twPopup/travel_rules_popup.asp"
                            data-width="600"
                            href="/twPopup/travel_rules_popup.asp"
                            target="policy"
                            data-analytics-json={
                                '{"applicationName":"userprofilemenu","elementPath":"travel-policy","action":"click","elementType":"link"}'
                            }
                        >
                            <Icon
                                ariaHidden
                                className={classnames(`${CSS_BLOCK}__icon`)}
                                iconName="decision"
                                size="sm"
                            />
                            {formatter.formattedMessage({ id: 'CoreUI.travelPolicy' })}
                        </Link>
                    </List.Item>
                )}
                {dataCenterLocation && (
                    <List.Item
                        className={classnames(`${CSS_BLOCK}__list-item`)}
                        data-test="user-profile-menu-service-status-link"
                    >
                        <ServiceStatusLink
                            className={classnames(`${CSS_BLOCK}__list-item-content`)}
                            innerTextClasses={classnames(`${CSS_BLOCK}__list-item-content-text`)}
                            dataCenterLocation={dataCenterLocation}
                            icon={(
                                <Icon
                                    ariaHidden
                                    className={classnames(`${CSS_BLOCK}__icon`)}
                                    iconName="crm-service-manager"
                                    size="sm"
                                />
                            )}
                        />
                    </List.Item>
                )}
                {supportLink && (
                    <List.Item
                        className={classnames(`${CSS_BLOCK}__list-item`)}
                        data-test="user-profile-menu-support-link"
                    >
                        <Link
                            className={classnames(`${CSS_BLOCK}__list-item-content`)}
                            innerTextClasses={classnames(`${CSS_BLOCK}__list-item-content-text`)}
                            href={supportLink}
                            target={
                                supportLink?.indexOf?.('window.open') === -1 ? '_blank' : undefined
                            }
                            data-analytics-json={
                                '{"applicationName":"userprofilemenu","elementPath":"contact-support-link","action":"click","elementType":"link"}'
                            }
                        >
                            <Icon
                                ariaHidden
                                className={classnames(`${CSS_BLOCK}__icon`)}
                                iconName="phone"
                                size="sm"
                            />
                            {formatter.formattedMessage({ id: 'CoreUI.contactSupport' })}
                        </Link>
                    </List.Item>
                )}
                <List.Item className={classnames(`${CSS_BLOCK}__list-item`)}>
                    <NavMenu
                        inToolbarOverflow
                        buttonContents={(
                            <span
                                className={classnames(
                                    `${CSS_BLOCK}__list-item-content`,
                                    `${CSS_BLOCK}__list-item-content--legal`,
                                )}
                            >
                                <Icon
                                    ariaHidden
                                    className={classnames(`${CSS_BLOCK}__icon`)}
                                    iconName="official-service"
                                    size="sm"
                                />

                                <span className={classnames(`${CSS_BLOCK}__list-item-text`)}>
                                    {formatter.formattedMessage({ id: 'CoreUI.legal' })}
                                </span>
                            </span>
                        )}
                        className={classnames(`${CSS_BLOCK}__list-item`)}
                        menuAriaLabel={formatter.formattedMessage({ id: 'CoreUI.legal' })}
                        tooltipMessage={formatter.formattedMessage({
                            id: 'CoreUI.legalTooltipMessage',
                        })}
                        onShow={() => {
                            setLegalOpen(true);
                        }}
                        onHide={() => {
                            setLegalOpen(false);
                        }}
                        iconProps={{
                            iconName: 'slim-arrow-right',
                            className: classnames(
                                `${CSS_BLOCK}__icon`,
                                `${CSS_BLOCK}__icon--legal`,
                            ),
                            forceSize: 'sm',
                        }}
                        buttonProps={{
                            className: classnames(`${CSS_BLOCK}__legal-button`, {
                                [`${CSS_BLOCK}__legal-button--active`]: legalOpen,
                            }),
                            innerProps: {
                                className: classnames(`${CSS_BLOCK}__legal-button-inner`),
                            },
                            innerTextClasses: classnames(`${CSS_BLOCK}__legal-button-inner-text`),
                            'data-test': 'user-profile-menu-legal-button',
                        }}
                        overlayProps={{
                            className: classnames(`${CSS_BLOCK}__overlay`),
                            targetProps: {
                                className: classnames(`${CSS_BLOCK}__legal-target`),
                            },
                            popperPlacement: [
                                'left-start',
                                'left-end',
                                'left',
                                'bottom-start',
                                'bottom-end',
                                'top-start',
                                'top-end',
                            ],
                        }}
                        onKeyDown={(event) => {
                            // this was focusing back on the profile button
                            if (event.key === 'ArrowRight') {
                                event.preventDefault();
                                event.stopPropagation();
                            }
                        }}
                    >
                        {controllerPrivacyStatementUrl && (
                            <NavMenu.Item
                                label={formatter.formattedMessage({
                                    id: 'CoreUI.controllerPrivacyStatement',
                                })}
                                href={`${controllerPrivacyStatementUrl}${controllerPrivacyStatementUrl.indexOf('?') !== -1 ? '&' : '?'
                                }lang=${lang ? lang.substring(0, 2) : 'en'}`}
                                data-test="controller-privacy-statement"
                                linkProps={{
                                    className: classnames(`${CSS_BLOCK}__list-item-content`),
                                    rel: 'noreferrer noopener',
                                    target: '_blank',
                                    'data-analytics-json':
                                        '{"applicationName":"userprofilemenu","elementPath":"privacy-statement","action":"click","elementType":"link"}',
                                    innerTextClasses: classnames(
                                        `${CSS_BLOCK}__list-item-content-text`,
                                    ),
                                }}
                            />
                        )}
                        {customerLegalDisclosureUrl && (
                            <NavMenu.Item
                                label={formatter.formattedMessage({ id: 'CoreUI.legalDisclosure' })}
                                href={customerLegalDisclosureUrl}
                                data-test="customer-legal-disclosure"
                                linkProps={{
                                    className: classnames(`${CSS_BLOCK}__list-item-content`),
                                    rel: 'noreferrer noopener',
                                    target: '_blank',
                                    'data-analytics-json':
                                        '{"applicationName":"userprofilemenu","elementPath":"legal-disclosure-statement","action":"click","elementType":"link"}',
                                    innerTextClasses: classnames(
                                        `${CSS_BLOCK}__list-item-content-text`,
                                    ),
                                }}
                            />
                        )}
                        {formatter?.preferences?.lang && asyncCookiePrefLink !== null
                            ? asyncCookiePrefLink
                            : null}
                        <NavMenu.Item
                            label={formatter.formattedMessage({ id: 'CoreUI.cookieStatement' })}
                            href={getCookieStatementUrl()}
                            data-test="cookie-statement-link"
                            linkProps={{
                                className: classnames(`${CSS_BLOCK}__list-item-content`),
                                rel: 'noreferrer noopener',
                                target: '_blank',
                                'data-analytics-json':
                                    '{"applicationName":"userprofilemenu","elementPath":"cookie-statement-link","action":"click","elementType":"link"}',
                                innerTextClasses: classnames(
                                    `${CSS_BLOCK}__list-item-content-text`,
                                ),
                            }}
                        />
                    </NavMenu>
                </List.Item>
                <List.Item
                    data-test="user-profile-menu-about-link"
                    className={classnames(`${CSS_BLOCK}__list-item`)}
                >
                    <span
                        className={classnames(`${CSS_BLOCK}__list-item-content`)}
                        onClick={() => showAboutDialogCallback(true)}
                        onKeyDown={(event) => {
                            if (isEnterOrSpace(event)) {
                                showAboutDialogCallback(true);
                            }
                        }}
                        role="button"
                        tabIndex={0}
                    >
                        <Icon
                            ariaHidden
                            className={classnames(`${CSS_BLOCK}__icon`)}
                            iconName="information"
                            size="sm"
                        />
                        <span className={classnames(`${CSS_BLOCK}__list-item-text`)}>
                            {formatter.formattedMessage({ id: 'CoreUI.profile.about' })}
                        </span>
                    </span>
                </List.Item>
                <List.Item className={classnames(`${CSS_BLOCK}__divider`)} divider />
                <List.Item className={classnames(`${CSS_BLOCK}__signout`)}>
                    <Button
                        type="tertiary"
                        onClick={onLogOut}
                        data-test="user-profile-menu-signout-link"
                    >
                        <Icon ariaHidden iconName="log" size="sm" />
                        {formatter.formattedMessage({ id: 'CoreUI.signOut' })}
                    </Button>
                </List.Item>
            </List>
        </div>
    );
};

UserProfileMenu.displayName = 'UserProfileMenu';

UserProfileMenu.cssBlock = CSS_BLOCK;

UserProfileMenu.propTypes = {
    actingAs: PropTypes.shape({
        allUsers: PropTypes.bool,
        companyName: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.string),
        otherUserName: PropTypes.string,
    }),
    baseUrl: PropTypes.string,
    onEndLoading: PropTypes.func,
    onStartLoading: PropTypes.func,
    profileUrl: PropTypes.string,
    actingAsQuickHelpRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    actingAsUserDropdownRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    styles: PropTypes.object,
    lang: PropTypes.string,
    showAboutDialogCallback: PropTypes.func,
    userProfileMenuProps: PropTypes.shape({
        controllerPrivacyStatementUrl: PropTypes.string,
        isTravelUser: PropTypes.bool,
        supportLink: PropTypes.string,
        dataCenterLocation: PropTypes.string,
        customerLegalDisclosureUrl: PropTypes.string,
        demo: PropTypes.bool,
    }),
    oshCacheIdentifier: PropTypes.string,
    isOpusUser: PropTypes.bool,
    isCompleteTheme: PropTypes.bool,
};

export default compose(withFormatter, withErrorBoundary)(UserProfileMenu);
